import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../store/hooks';
import { setTokens } from '../../store/slices/authSlice';
import { toast } from 'react-toastify';
import { SERVER_URL } from '../../config/env';
import { toSameOriginPath } from '../../utils/sameOriginPath';
import { shouldShowWelcome } from '../../features/welcome';

/**
 * OAuth callback handler
 * 
 * Handles OAuth authorization code callback:
 * - Exchanges authorization code for tokens via /oauth/token
 * - Validates PKCE code_verifier if present
 * - Stores tokens and redirects to home
 * 
 * If no valid params (e.g. user pressed back button), redirects home.
 */
const OAuthCallback: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [error, setError] = useState<string | null>(null);
    const [processing, setProcessing] = useState(true);

    useEffect(() => {
        const handleCallback = async () => {
            // the iframe-based silent-auth branch that used to sit here is gone
            // along with attemptSilentAuth(): the IdP's X-Frame-Options and CSP
            // frame-ancestors made it impossible to frame, so it never once
            // succeeded. clear the stale flag if an old tab left one behind.
            sessionStorage.removeItem('silent_auth');

            // check for error from auth service
            const errorParam = searchParams.get('error');
            if (errorParam) {
                const errorDesc = searchParams.get('error_description') || errorParam;
                setError(errorDesc);
                setProcessing(false);
                return;
            }

            // check for authorization code (OAuth flow)
            const code = searchParams.get('code');
            const state = searchParams.get('state');

            if (code) {
                try {
                    // verify state if we stored one
                    const savedState = sessionStorage.getItem('oauth_state');
                    if (savedState && state !== savedState) {
                        throw new Error('Invalid state - possible CSRF attack');
                    }

                    // get PKCE verifier
                    const codeVerifier = sessionStorage.getItem('pkce_verifier');

                    const redirectUri = `${window.location.origin}/auth/callback`;

                    const bffBase = SERVER_URL ? `${SERVER_URL}/api/auth` : '/api/auth';
                    const bffResponse = await fetch(`${bffBase}/exchange-code`, {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            code,
                            codeVerifier: codeVerifier || '',
                            redirectUri,
                        }),
                    });

                    const bffData = await bffResponse.json().catch(() => null);
                    if (!bffResponse.ok) {
                        const message = bffData?.error || bffData?.message || 'BFF code exchange failed';
                        throw new Error(message);
                    }

                    if (!bffData?.authenticated || !bffData?.accessToken) {
                        throw new Error('BFF response missing accessToken');
                    }

                    const accessToken = bffData.accessToken;
                    console.log('[OAuthCallback] Code exchanged via backend BFF');

                    // cleanup
                    const returnTo = sessionStorage.getItem('oauth_return_to');
                    sessionStorage.removeItem('oauth_state');
                    sessionStorage.removeItem('pkce_verifier');
                    sessionStorage.removeItem('oauth_return_to');
                    // NOTE: the loop counter is deliberately NOT cleared here.
                    // Exchanging the code is a step *inside* the silent re-auth
                    // loop, not evidence of escaping it — the exchange succeeds
                    // on every lap. Clearing it here meant the counter never got
                    // past 1 and the guard could never trip. App.js resets it
                    // once a bootstrapped session actually works.

                    // store access token (refresh token held server-side in BFF session)
                    console.log('[OAuthCallback] Storing access token:', {
                        hasAccessToken: !!accessToken,
                        refreshTokenStorage: 'HttpOnly cookie (not accessible to JS)',
                    });
                    dispatch(setTokens({
                        accessToken: accessToken,
                    }));

                    // a new account goes through the welcome screen first
                    // (emoji, birthday), which ends on the home page
                    if (await shouldShowWelcome(accessToken)) {
                        navigate('/welcome', { replace: true });
                        return;
                    }

                    toast.success('Welcome!');

                    // go back to the page the user was on when the session
                    // expired. this always navigated to '/', so the return URL
                    // SessionManager carefully built was thrown away and the
                    // user had to find their way back — and re-trigger whatever
                    // failed there in the first place.
                    navigate(toSameOriginPath(returnTo) ?? '/', { replace: true });
                } catch (err: any) {
                    console.error('OAuth callback error:', err);
                    setError(err.message || 'Authentication failed');
                    setProcessing(false);
                }
                return;
            }

            // no valid params - user probably pressed back button
            // just redirect to home (or login if not authenticated)
            navigate('/');
        };

        handleCallback();
    }, [searchParams, dispatch, navigate]);

    if (error) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                backgroundColor: '#fdbe7c',
                padding: '20px',
            }}>
                <h2 style={{ color: '#e40000', marginBottom: '20px' }}>Authentication Error</h2>
                <p style={{ marginBottom: '20px' }}>{error}</p>
                <button
                    onClick={() => navigate('/login')}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#f4ab47',
                        border: '1px solid black',
                        cursor: 'pointer',
                    }}
                >
                    Try Again
                </button>
            </div>
        );
    }

    if (processing) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                backgroundColor: '#fdbe7c',
            }}>
                <p>Authenticating...</p>
            </div>
        );
    }

    return null;
};

export default OAuthCallback;
